/**
 * Building Codes Module
 *
 * Main API for generating building code boilerplate language
 * for construction documents across California jurisdictions.
 *
 * Usage:
 *   import { generateBoilerplate, getAvailableJurisdictions } from './data/buildingCodes';
 *
 *   const jurisdictions = getAvailableJurisdictions();
 *   const boilerplate = generateBoilerplate('city-of-la', ['kitchen', 'bathroom']);
 */

import { BASE_SECTIONS, CALIFORNIA_CODES } from './californiaBase';
import { CITY_OF_LA } from './jurisdictions/cityOfLa';
import { LA_COUNTY } from './jurisdictions/laCounty';
import { PROJECT_SCOPES, expandScopes, COMMON_SCOPE_PRESETS } from './scopes';

// Registry of all available jurisdictions
const JURISDICTIONS = {
  'city-of-la': CITY_OF_LA,
  'la-county': LA_COUNTY,
};

/**
 * Get list of all available jurisdictions
 * @returns {Array} Array of jurisdiction objects with id, name, shortName
 */
export const getAvailableJurisdictions = () => {
  return Object.values(JURISDICTIONS).map((j) => ({
    id: j.id,
    name: j.jurisdiction.name,
    shortName: j.jurisdiction.shortName,
    department: j.jurisdiction.department,
  }));
};

/**
 * Get jurisdiction configuration by ID
 * @param {string} jurisdictionId - Jurisdiction identifier
 * @returns {Object|null} Jurisdiction configuration or null
 */
export const getJurisdiction = (jurisdictionId) => {
  return JURISDICTIONS[jurisdictionId] || null;
};

/**
 * Replace placeholders in text with jurisdiction-specific values
 * @param {string} text - Text with placeholders
 * @param {Object} jurisdiction - Jurisdiction configuration
 * @returns {string} Formatted text
 */
const formatText = (text, jurisdiction) => {
  const { codeYear, localCodeRef } = jurisdiction.codeAdoption;
  return text
    .replace(/{codeYear}/g, codeYear)
    .replace(/{localCodeRef}/g, localCodeRef || '');
};

/**
 * Format a section item (handles both string and object items)
 * @param {string|Object} item - Item to format
 * @param {Object} jurisdiction - Jurisdiction configuration
 * @returns {Object} Formatted item with text and optional subItems
 */
const formatItem = (item, jurisdiction) => {
  if (typeof item === 'string') {
    return {
      text: formatText(item, jurisdiction),
      subItems: null,
    };
  }
  return {
    text: formatText(item.text, jurisdiction),
    subItems: item.subItems
      ? item.subItems.map((sub) => formatText(sub, jurisdiction))
      : null,
  };
};

/**
 * Filter sections based on project scope
 * @param {Array} sections - Array of section objects
 * @param {Array} scopes - Array of scope strings
 * @returns {Array} Filtered sections
 */
const filterSectionsByScope = (sections, scopes) => {
  return sections.filter((section) => {
    // Include if no scope restriction (empty array) or if any scope matches
    if (!section.scope || section.scope.length === 0) return true;
    return section.scope.some((s) => scopes.includes(s));
  });
};

/**
 * Merge base sections with jurisdiction overrides and additions
 * @param {Object} jurisdiction - Jurisdiction configuration
 * @param {Array} expandedScopes - Expanded scope array
 * @returns {Array} Merged and filtered sections
 */
const mergeSections = (jurisdiction, expandedScopes) => {
  const sections = [];

  // 1. Add jurisdiction's code compliance section first (always)
  if (jurisdiction.codeComplianceSection) {
    sections.push(jurisdiction.codeComplianceSection);
  }

  // 2. Process base sections
  BASE_SECTIONS.forEach((baseSection) => {
    // Check if jurisdiction overrides this section entirely
    if (
      jurisdiction.sectionOverrides &&
      jurisdiction.sectionOverrides[baseSection.id]
    ) {
      sections.push(jurisdiction.sectionOverrides[baseSection.id]);
    } else {
      // Use base section, potentially with appended items
      const section = { ...baseSection };

      // Append jurisdiction-specific items if any
      if (
        jurisdiction.sectionAppends &&
        jurisdiction.sectionAppends[baseSection.id]
      ) {
        section.items = [
          ...section.items,
          ...jurisdiction.sectionAppends[baseSection.id],
        ];
      }

      sections.push(section);
    }
  });

  // 3. Add jurisdiction-specific additional sections
  if (jurisdiction.additionalSections) {
    sections.push(...jurisdiction.additionalSections);
  }

  // 4. Filter by scope
  return filterSectionsByScope(sections, expandedScopes);
};

/**
 * Generate formatted boilerplate for a project
 *
 * @param {string} jurisdictionId - Jurisdiction identifier (e.g., 'city-of-la')
 * @param {Array} projectScopes - Array of scope strings (e.g., ['kitchen', 'bathroom'])
 * @returns {Object|null} Formatted boilerplate with metadata and sections, or null if jurisdiction not found
 *
 * @example
 * const boilerplate = generateBoilerplate('city-of-la', ['kitchen']);
 * // Returns:
 * // {
 * //   jurisdiction: { id, name, shortName, department, ... },
 * //   codeInfo: { year, effectiveDate, notes },
 * //   sections: [{ id, title, items: [{ number, text, subItems }] }],
 * //   generatedAt: ISO timestamp
 * // }
 */
export const generateBoilerplate = (jurisdictionId, projectScopes = []) => {
  const jurisdiction = getJurisdiction(jurisdictionId);
  if (!jurisdiction) {
    console.warn(`Jurisdiction not found: ${jurisdictionId}`);
    return null;
  }

  // Expand scopes to include triggered scopes
  const expandedScopes = expandScopes(projectScopes);

  // Merge and filter sections
  const mergedSections = mergeSections(jurisdiction, expandedScopes);

  // Format all sections
  const formattedSections = mergedSections.map((section) => ({
    id: section.id,
    title: section.title,
    items: section.items.map((item, index) => ({
      number: index + 1,
      ...formatItem(item, jurisdiction),
    })),
  }));

  return {
    jurisdiction: {
      id: jurisdiction.id,
      name: jurisdiction.jurisdiction.name,
      shortName: jurisdiction.jurisdiction.shortName,
      department: jurisdiction.jurisdiction.department,
      website: jurisdiction.jurisdiction.website,
      permitPortal: jurisdiction.jurisdiction.permitPortal,
      phone: jurisdiction.jurisdiction.phone,
    },
    codeInfo: {
      year: jurisdiction.codeAdoption.codeYear,
      effectiveDate: jurisdiction.codeAdoption.effectiveDate,
      localAmendments: jurisdiction.codeAdoption.localAmendments,
      notes: jurisdiction.codeAdoption.notes,
    },
    scopes: {
      selected: projectScopes,
      expanded: expandedScopes,
    },
    sections: formattedSections,
    generatedAt: new Date().toISOString(),
  };
};

/**
 * Generate a plain text version of the boilerplate
 * Useful for simple display or copying
 *
 * @param {string} jurisdictionId - Jurisdiction identifier
 * @param {Array} projectScopes - Array of scope strings
 * @returns {string} Plain text formatted boilerplate
 */
export const generateBoilerplateText = (jurisdictionId, projectScopes = []) => {
  const boilerplate = generateBoilerplate(jurisdictionId, projectScopes);
  if (!boilerplate) return '';

  const lines = [];

  lines.push('GENERAL NOTES');
  lines.push(`Jurisdiction: ${boilerplate.jurisdiction.name}`);
  lines.push(`Code Year: ${boilerplate.codeInfo.year}`);
  lines.push('');

  boilerplate.sections.forEach((section) => {
    lines.push(section.title);
    lines.push('-'.repeat(section.title.length));

    section.items.forEach((item) => {
      lines.push(`${item.number}. ${item.text}`);
      if (item.subItems) {
        item.subItems.forEach((sub) => {
          lines.push(`   - ${sub}`);
        });
      }
    });

    lines.push('');
  });

  return lines.join('\n');
};

// Re-export utilities
export { PROJECT_SCOPES, expandScopes, COMMON_SCOPE_PRESETS } from './scopes';
export { CALIFORNIA_CODES } from './californiaBase';

export default {
  generateBoilerplate,
  generateBoilerplateText,
  getAvailableJurisdictions,
  getJurisdiction,
  PROJECT_SCOPES,
  COMMON_SCOPE_PRESETS,
  CALIFORNIA_CODES,
};
